import React from 'react';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Numeric } from '@swyftx/aviary/atoms/Typography';
import { NumericProps } from '@swyftx/aviary/atoms/Typography/Numeric/Numeric.styles';
import { MarginRisk } from '@swyftx/margin/build/types';

import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';
import { cn } from '@shared/utils/lib/ui';

import { useMarginAccount } from 'src/lib/margin/hooks/account';
import { getMarginRiskColor } from 'src/lib/margin/utils';

import { MarginAccountHealthBarElement } from './MarginAccountHealthBarElement';
import { MarginAccountHealthBarMarks } from './MarginAccountHealthBarMarks';
import { MarginAccountHealthBarThresholds } from './MarginAccountHealthBarThresholds';

type Props = {
  variant?: 'compact' | 'standard';
  stepIncrement?: number;
  maxIncrement?: number;
  customMarginLevel?: Big;
  customRiskRating?: MarginRisk;
  showMarginLevel?: boolean;
  className?: string;
  fontSize?: NumericProps['size'];
};

const MarginAccountHealthBar: React.FC<Props> = ({
  variant = 'standard',
  stepIncrement = 10,
  maxIncrement = 250,
  showMarginLevel = false,
  customMarginLevel,
  customRiskRating,
  className,
  fontSize = 'small',
}) => {
  const { marginLevel, usedMargin } = useMarginAccount();
  if (variant === 'compact') {
    return (
      <FlexLayout direction='column' spacing={2} className={className}>
        <FlexLayout spacing={4} alignItems='center'>
          {showMarginLevel && (
            <Numeric size={fontSize} weight='bold' color={getMarginRiskColor(usedMargin, marginLevel)}>
              {formatCurrency(marginLevel, undefined, { priceScale: 2 })}%
            </Numeric>
          )}

          <FlexLayout className='relative h-8 w-[76px] overflow-hidden rounded-2 bg-color-background-surface-secondary'>
            <MarginAccountHealthBarElement
              stepIncrement={stepIncrement}
              maxIncrement={maxIncrement}
              customMarginLevel={customMarginLevel}
              customRiskRating={customRiskRating}
              className='absolute bottom-0 top-0 w-[76px] rounded-2 transition-all duration-1000 delay-200'
            />
          </FlexLayout>
        </FlexLayout>
      </FlexLayout>
    );
  }

  return (
    <FlexLayout className={cn('mt-12 w-full', className)} direction='column' justifyContent='end' spacing={2}>
      <FlexLayout className='relative w-full' direction='column' justifyContent='end'>
        <FlexLayout alignItems='center' justifyContent='space-between'>
          <MarginAccountHealthBarMarks stepIncrement={stepIncrement} maxIncrement={maxIncrement} size={fontSize} />
        </FlexLayout>
        <MarginAccountHealthBarElement
          stepIncrement={stepIncrement}
          maxIncrement={maxIncrement}
          customMarginLevel={customMarginLevel}
          customRiskRating={customRiskRating}
          className='absolute bottom-10 top-10 rounded-2 transition-all duration-1000 delay-200'
        />
      </FlexLayout>
      <MarginAccountHealthBarThresholds
        maxIncrement={maxIncrement}
        stepIncrement={stepIncrement}
        customRiskRating={customRiskRating}
      />
    </FlexLayout>
  );
};

export { MarginAccountHealthBar };
